import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  Space,
  Typography,
  Input,
  Tag,
  Tooltip,
  Empty,
  Spin,
  Alert,
  Select,
  Segmented,
  message,
  Popconfirm,
  Dropdown,
  Modal,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  CopyOutlined,
  CheckOutlined,
  CloseOutlined,
  StopOutlined,
  EditOutlined,
  DeleteOutlined,
  WarningFilled,
  ApartmentOutlined,
  ExportOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { env } from '@/lib/env';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { SectionCard } from '@/components/SectionCard';
import {
  getTestPromptsForAgent,
  removeTestPrompt,
  markTestPromptResult,
  type AgentTestPrompt,
} from '@/api/agentTestPrompts';
import { sendTestPromptToSession } from '@/api/agents';
import { TestPromptEditorDrawer } from './TestPromptEditorDrawer';

const { Text, Paragraph } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// TestPromptsTab — manual-QA library on Agent detail.
//
// Layout: split view, optimized for "I'm about to test the agent" mode.
//
//   ┌──────────────────────────┬────────────────────────────────────────────┐
//   │  SIDEBAR (260px)         │  MAIN (per-prompt cards)                   │
//   │  • Search box            │  Card: Title · Code · badges               │
//   │  • Role filter           │        Prompt body  [Copy →]               │
//   │  • Category tree         │        What this tests · What to look for  │
//   │    (counts per node)     │        Mark pass/fail/skip                 │
//   └──────────────────────────┴────────────────────────────────────────────┘
//
// The "Last result" stamp on each card means the user can come back tomorrow
// and see what they tested today. Stale prompts (not run in 30+ days) get
// a subtle dim treatment.
// ─────────────────────────────────────────────────────────────────────────────

export const TestPromptsTab = observer(function TestPromptsTab() {
  const { agentDetailStore: detail, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const agent = detail.agent;

  const [prompts, setPrompts] = useState<AgentTestPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter / search state — all client-side; the per-agent batch is small.
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);

  // Editor drawer state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Page-level test-session settings modal — same localStorage keys as the
  // per-card modal. Lets the user change the configured ids without having
  // to scroll to a card and open its chevron menu.
  const PATIENT_KEY = 'agentstudio.testSession.patientMemberId';
  const PROVIDER_KEY = 'agentstudio.testSession.providerEmployeeId';
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsPatientId, setSettingsPatientId] = useState('');
  const [settingsProviderId, setSettingsProviderId] = useState('');

  // Dual-audience routing override — only meaningful when the owning agent
  // has BOTH patientApp and merchantApp on. Per-prompt `role` still wins
  // when set; this controls only the role-less fallback for every card on
  // the page. Persisted per-agent in localStorage so a tester's choice
  // sticks across reloads, but stays scoped to their browser. Single-
  // audience agents (the common case) ignore this entirely — their target
  // is deterministic from the agent flags alone.
  const dualTargetKey = agent ? `agentstudio.testPrompts.dualTarget.${agent.id}` : '';
  const [dualTarget, setDualTarget] = useState<'patient' | 'provider'>('patient');
  useEffect(() => {
    if (!dualTargetKey) return;
    const stored = localStorage.getItem(dualTargetKey);
    setDualTarget(stored === 'provider' ? 'provider' : 'patient');
  }, [dualTargetKey]);
  const setDualTargetPersisted = (next: 'patient' | 'provider') => {
    setDualTarget(next);
    if (dualTargetKey) localStorage.setItem(dualTargetKey, next);
  };
  const isDualAudience = !!agent?.patientApp && !!agent?.merchantApp;

  const openSettings = () => {
    setSettingsPatientId(localStorage.getItem(PATIENT_KEY) ?? '');
    setSettingsProviderId(localStorage.getItem(PROVIDER_KEY) ?? '');
    setSettingsOpen(true);
  };

  const saveSettings = () => {
    const pid = settingsPatientId.trim();
    const eid = settingsProviderId.trim();
    if (pid) localStorage.setItem(PATIENT_KEY, pid);
    else localStorage.removeItem(PATIENT_KEY);
    if (eid) localStorage.setItem(PROVIDER_KEY, eid);
    else localStorage.removeItem(PROVIDER_KEY);
    setSettingsOpen(false);
    void message.success('Test session ids updated.');
  };

  const refresh = async () => {
    if (!agent) return;
    setLoading(true);
    setError(null);
    try {
      setPrompts(await getTestPromptsForAgent(agent.id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id]);

  // ── Derived: distinct roles + category tree ─────────────────────────
  const roles = useMemo(
    () =>
      [...new Set(prompts.map((p) => p.role).filter(Boolean) as string[])].sort((a, b) =>
        a.localeCompare(b),
      ),
    [prompts],
  );

  // category → subcategory → count, ordered alphabetically.
  const categoryTree = useMemo(() => {
    const m = new Map<string, Map<string, number>>();
    for (const p of prompts) {
      const cat = p.category ?? '(uncategorized)';
      const sub = p.subcategory ?? '';
      if (!m.has(cat)) m.set(cat, new Map());
      m.get(cat)!.set(sub, (m.get(cat)!.get(sub) ?? 0) + 1);
    }
    return [...m.entries()]
      .map(([cat, subs]) => ({
        category: cat,
        total: [...subs.values()].reduce((a, b) => a + b, 0),
        subs: [...subs.entries()]
          .map(([sub, count]) => ({ subcategory: sub, count }))
          .sort((a, b) => a.subcategory.localeCompare(b.subcategory)),
      }))
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [prompts]);

  // ── Derived: filtered list ──────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return prompts.filter((p) => {
      if (selectedCategory && (p.category ?? '(uncategorized)') !== selectedCategory) return false;
      if (selectedSubcategory != null && (p.subcategory ?? '') !== selectedSubcategory) return false;
      if (roleFilter && p.role !== roleFilter) return false;
      if (!q) return true;
      return [p.title, p.code, p.prompt, p.whatThisTests, p.whatToLookFor, p.tags]
        .filter(Boolean)
        .some((s) => (s as string).toLowerCase().includes(q));
    });
  }, [prompts, search, selectedCategory, selectedSubcategory, roleFilter]);

  const handleDelete = async (p: AgentTestPrompt) => {
    try {
      await removeTestPrompt(p.id);
      void message.success('Removed');
      void refresh();
    } catch (err) {
      void message.error('Delete failed: ' + (err as Error).message);
    }
  };

  const handleMark = async (p: AgentTestPrompt, status: 'pass' | 'fail' | 'skip') => {
    try {
      const updated = await markTestPromptResult(p.id, status);
      // Optimistic in-place update so the user sees the stamp immediately
      setPrompts((prev) => prev.map((x) => (x.id === p.id ? updated : x)));
      void message.success(`Marked ${status}`);
    } catch (err) {
      void message.error('Could not save result: ' + (err as Error).message);
    }
  };

  if (!agent) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
      <SectionCard
        title={
          <Space size={8}>
            <span>Test prompts</span>
            <Tag color="violet">{prompts.length}</Tag>
          </Space>
        }
        subtitle="Copy-paste-ready prompts for manually testing this agent. Pass/fail stamps are remembered per tester."
        actions={
          <Space>
            <Tooltip title="Change the patient member id / provider employee id used by 'Test in {patient|provider} app'">
              <Button icon={<SettingOutlined />} onClick={openSettings}>
                Test session
              </Button>
            </Tooltip>
            <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingId(null);
                setEditorOpen(true);
              }}
            >
              New test prompt
            </Button>
          </Space>
        }
      >
        {error && <Alert type="error" showIcon message={error} closable style={{ marginBottom: 12 }} />}

        {loading && prompts.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : prompts.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No test prompts yet. Click New test prompt to author the first one — or import the legacy Markdown bundle for this agent."
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '260px minmax(0, 1fr)',
              gap: 16,
              alignItems: 'start',
            }}
          >
            {/* ── Sidebar ─────────────────────────────────────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Input
                placeholder="Search prompts…"
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
              />
              <Select<string>
                placeholder="All roles"
                allowClear
                value={roleFilter ?? undefined}
                onChange={(v) => setRoleFilter(v ?? null)}
                style={{ width: '100%' }}
                options={roles.map((r) => ({ value: r, label: r }))}
              />
              <CategoryTree
                tree={categoryTree}
                selectedCategory={selectedCategory}
                selectedSubcategory={selectedSubcategory}
                onSelect={(cat, sub) => {
                  setSelectedCategory(cat);
                  setSelectedSubcategory(sub);
                }}
                isDark={isDark}
              />
            </div>

            {/* ── Main: prompt cards ──────────────────────────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {filtered.length === prompts.length
                    ? `${prompts.length} prompts`
                    : `${filtered.length} of ${prompts.length} prompts`}
                </Text>
                {(selectedCategory || selectedSubcategory != null || roleFilter || search) && (
                  <Button
                    size="small"
                    type="link"
                    onClick={() => {
                      setSelectedCategory(null);
                      setSelectedSubcategory(null);
                      setRoleFilter(null);
                      setSearch('');
                    }}
                  >
                    Clear filters
                  </Button>
                )}
                {/* Dual-audience routing override. Only surfaces when the
                    agent serves both patients and care team — single-
                    audience agents have a deterministic answer and don't
                    need the toggle. Per-prompt `role` still wins when set;
                    this is the fallback for role-less prompts. */}
                {isDualAudience && (
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Tooltip
                      title={
                        <span>
                          This agent serves both audiences. Prompts with an
                          explicit role always route to that side; prompts
                          without a role default to whichever side you pick
                          here. Stored per-agent in this browser.
                        </span>
                      }
                    >
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Default Test target:
                      </Text>
                    </Tooltip>
                    <Segmented
                      size="small"
                      value={dualTarget}
                      onChange={(v) => setDualTargetPersisted(v as 'patient' | 'provider')}
                      options={[
                        { label: 'Patient', value: 'patient' },
                        { label: 'Provider', value: 'provider' },
                      ]}
                    />
                  </div>
                )}
              </div>

              {filtered.length === 0 ? (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="No prompts match these filters."
                />
              ) : (
                filtered.map((p) => (
                  <PromptCard
                    key={p.id}
                    prompt={p}
                    isDark={isDark}
                    dualAudienceFallback={dualTarget}
                    onEdit={() => {
                      setEditingId(p.id);
                      setEditorOpen(true);
                    }}
                    onDelete={() => handleDelete(p)}
                    onMark={(status) => handleMark(p, status)}
                  />
                ))
              )}
            </div>
          </div>
        )}
      </SectionCard>

      <TestPromptEditorDrawer
        open={editorOpen}
        agentId={agent.id}
        promptId={editingId}
        existingTitles={prompts
          .filter((p) => p.id !== editingId && p.title)
          .map((p) => p.title!.toLowerCase())}
        onClose={() => setEditorOpen(false)}
        onSaved={() => {
          setEditorOpen(false);
          void refresh();
        }}
      />

      {/* Page-level test session settings — open from the "Test session"
          button in the action bar. Same localStorage keys as the per-card
          modal so the ids stay in sync. */}
      <Modal
        open={settingsOpen}
        title="Test session settings"
        okText="Save"
        cancelText="Cancel"
        onOk={saveSettings}
        onCancel={() => setSettingsOpen(false)}
        destroyOnHidden
      >
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Configure which user the patient/provider tab is signed in as.
          When you click "Test in patient/provider app" on any prompt card,
          the prompt is sent via SignalR to the open tab matching that id.
        </Paragraph>
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Sign into the patient/provider app first, then open DevTools — the
          console logs the user id you need.
        </Paragraph>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Patient app — member id
            </Text>
            <Input
              placeholder="e.g. 1234"
              value={settingsPatientId}
              onChange={(e) => setSettingsPatientId(e.target.value)}
              onPressEnter={saveSettings}
              autoFocus
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Provider app — employee id
            </Text>
            <Input
              placeholder="e.g. 5678"
              value={settingsProviderId}
              onChange={(e) => setSettingsProviderId(e.target.value)}
              onPressEnter={saveSettings}
            />
          </div>
        </div>
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
          Stored in this browser only (<code>localStorage</code>). Leave a
          field blank to clear it.
        </Paragraph>
      </Modal>
    </div>
  );
});

// ── Sidebar tree ───────────────────────────────────────────────────────────

interface TreeNode {
  category: string;
  total: number;
  subs: { subcategory: string; count: number }[];
}

function CategoryTree({
  tree,
  selectedCategory,
  selectedSubcategory,
  onSelect,
  isDark,
}: {
  tree: TreeNode[];
  selectedCategory: string | null;
  selectedSubcategory: string | null;
  onSelect: (cat: string | null, sub: string | null) => void;
  isDark: boolean;
}) {
  const allTotal = tree.reduce((acc, n) => acc + n.total, 0);
  const baseRow = {
    padding: '6px 8px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    display: 'flex' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    gap: 6,
  };
  const activeBg = isDark ? colors.violet[700] + '40' : colors.violet[100];
  const hoverBg = isDark ? colors.slate[700] : colors.slate[100];

  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 8,
        padding: 8,
      }}
    >
      <div
        style={{
          ...baseRow,
          background: !selectedCategory ? activeBg : 'transparent',
          fontWeight: !selectedCategory ? 600 : 400,
        }}
        onClick={() => onSelect(null, null)}
        onMouseEnter={(e) => {
          if (selectedCategory) e.currentTarget.style.background = hoverBg;
        }}
        onMouseLeave={(e) => {
          if (selectedCategory) e.currentTarget.style.background = 'transparent';
        }}
      >
        <span><ApartmentOutlined style={{ marginRight: 6 }} />All</span>
        <Tag>{allTotal}</Tag>
      </div>
      {tree.map((n) => {
        const isSelectedCat = selectedCategory === n.category && selectedSubcategory == null;
        return (
          <div key={n.category}>
            <div
              style={{
                ...baseRow,
                background: isSelectedCat ? activeBg : 'transparent',
                fontWeight: isSelectedCat ? 600 : 500,
                marginTop: 4,
              }}
              onClick={() => onSelect(n.category, null)}
              onMouseEnter={(e) => {
                if (!isSelectedCat) e.currentTarget.style.background = hoverBg;
              }}
              onMouseLeave={(e) => {
                if (!isSelectedCat) e.currentTarget.style.background = 'transparent';
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {n.category}
              </span>
              <Tag>{n.total}</Tag>
            </div>
            {selectedCategory === n.category &&
              n.subs.map((s) => {
                const isSelectedSub =
                  selectedCategory === n.category && selectedSubcategory === s.subcategory;
                return (
                  <div
                    key={s.subcategory}
                    style={{
                      ...baseRow,
                      background: isSelectedSub ? activeBg : 'transparent',
                      paddingLeft: 22,
                      fontSize: 12.5,
                      fontWeight: isSelectedSub ? 600 : 400,
                    }}
                    onClick={() => onSelect(n.category, s.subcategory)}
                    onMouseEnter={(e) => {
                      if (!isSelectedSub) e.currentTarget.style.background = hoverBg;
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelectedSub) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.subcategory || '(no subcategory)'}
                    </span>
                    <Tag>{s.count}</Tag>
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );
}

// ── Per-prompt card ────────────────────────────────────────────────────────

const RESULT_TONE: Record<string, { bg: string; fg: string; label: string }> = {
  pass: { bg: '#D1FAE5', fg: '#065F46', label: 'Pass' },
  fail: { bg: '#FEE2E2', fg: '#991B1B', label: 'Fail' },
  skip: { bg: '#F3F4F6', fg: '#4B5563', label: 'Skip' },
};

function PromptCard({
  prompt,
  isDark,
  dualAudienceFallback,
  onEdit,
  onDelete,
  onMark,
}: {
  prompt: AgentTestPrompt;
  isDark: boolean;
  /** For dual-audience agents (both patientApp + merchantApp on), the page-
   *  level toggle picks the default for role-less prompts. Single-audience
   *  agents ignore this — their target is determined by the agent flags. */
  dualAudienceFallback: 'patient' | 'provider';
  onEdit: () => void;
  onDelete: () => void;
  onMark: (status: 'pass' | 'fail' | 'skip') => void;
}) {
  // Source of truth for the owner agent: agentDetailStore.agent — the full
  // record loaded by getAgentById on this page. Previously we looked up
  // via agentRegistryStore.agents (merchant-scoped Search response), but
  // that turned out to be a slimmer projection in some cases — it has
  // displayName but A2AAgentName came through empty for agent 99
  // (FOLFOX Patient Symptom Agent) even though the DB row had it.
  // The detail-store record is canonical: same shape the AgentDetailPage
  // header renders, so if a2AAgentName shows there it's available here.
  const { agentDetailStore: detailStore } = useStore();
  const ownerAgent = detailStore.agent;

  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(prompt.prompt);
      setCopied(true);
      void message.success('Copied prompt to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      void message.error('Could not copy — clipboard blocked');
    }
  };

  // Deep-links to the consumer apps. Both URLs carry:
  //   * prefilledPrompt    — text for the chat input
  //   * agentId            — the platform agent id (ai.AiAgent.Id)
  //   * agentName          — the A2A agent name string the patient/provider
  //                          apps use to route conversations (e.g. "DiabetesAgent").
  //                          This is what `chatHelper.openChat()` expects.
  //   * agentDisplayName   — human-readable label for the chat header
  // The chosen default app depends on the prompt's Role.
  const buildLink = (base: string) => {
    if (!base) return null;
    const params = new URLSearchParams({
      prefilledPrompt: prompt.prompt,
      agentId: String(prompt.aiAgentId),
    });
    if (ownerAgent?.a2AAgentName) params.set('agentName', ownerAgent.a2AAgentName);
    if (ownerAgent?.displayName) params.set('agentDisplayName', ownerAgent.displayName);
    return `${base.replace(/\/$/, '')}/?${params.toString()}`;
  };
  const patientLink = useMemo(() => buildLink(env.patientAppUrl), [prompt.prompt, prompt.aiAgentId]);
  const providerLink = useMemo(() => buildLink(env.providerAppUrl), [prompt.prompt, prompt.aiAgentId]);

  // Default routing — the owning agent's audience is the primary signal,
  // because that's what the page header advertises and what testers expect.
  // A patient-only agent (patientApp && !merchantApp) ALWAYS opens in the
  // patient app, even for prompts whose role is unset or non-patient — the
  // prompt is being tested against a patient-facing agent, period. Same
  // mirror for provider-only agents.
  //
  // Dual-audience agents (both flags on) are the interesting case: a
  // prompt with an explicit role still routes by role (patient → patient
  // app; anything else → provider app), but a role-less prompt has no
  // signal of its own. There we honor the page-level "Default Test target"
  // segmented toggle (dualAudienceFallback), which the tester sets once
  // and which is persisted per-agent in localStorage. This lets a tester
  // sweep a dual-audience agent's prompts to one side without role-tagging
  // every single one.
  const agentIsPatientOnly = !!ownerAgent?.patientApp && !ownerAgent?.merchantApp;
  const agentIsProviderOnly = !!ownerAgent?.merchantApp && !ownerAgent?.patientApp;
  const role = (prompt.role ?? '').trim().toLowerCase();
  const hasExplicitRole = role.length > 0;
  const isPatientRole = role === 'patient';
  const defaultTarget: 'patient' | 'provider' =
    agentIsPatientOnly
      ? 'patient'
      : agentIsProviderOnly
      ? 'provider'
      : hasExplicitRole
      ? isPatientRole
        ? 'patient'
        : 'provider'
      : dualAudienceFallback;
  const defaultLink = defaultTarget === 'patient' ? patientLink : providerLink;
  const defaultLabel = defaultTarget === 'patient' ? 'Test in patient app' : 'Test in provider app';

  const openLink = (url: string | null) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // ── Send to the open patient/provider tab via SignalR ─────────────────
  // Targeting model: each tester configures, ONCE per browser, the user id
  // they're impersonating in the patient app (member id) and/or the provider
  // app (employee id). We persist them in localStorage and read them on each
  // send — so the tester signs into the patient app as ANY patient (e.g. a
  // test fixture like Eric Smith), grabs the user id from the browser
  // console (the patient app logs it on connect), pastes it into the
  // configure modal here, and from then on every "Test in patient app" click
  // lands a SignalR broadcast in that exact tab. No id-matching gymnastics.
  //
  // Storage keys (single source of truth — modal reads/writes these and
  // sendToSession reads them fresh on every click):
  //   agentstudio.testSession.patientMemberId
  //   agentstudio.testSession.providerEmployeeId
  const PATIENT_KEY = 'agentstudio.testSession.patientMemberId';
  const PROVIDER_KEY = 'agentstudio.testSession.providerEmployeeId';

  const getStoredId = (target: 'patient' | 'provider'): string | null => {
    const v = localStorage.getItem(target === 'patient' ? PATIENT_KEY : PROVIDER_KEY);
    return v && v.trim() ? v.trim() : null;
  };

  const [configureOpen, setConfigureOpen] = useState(false);
  const [pendingTarget, setPendingTarget] = useState<'patient' | 'provider' | null>(null);
  const [patientIdInput, setPatientIdInput] = useState<string>(
    () => localStorage.getItem(PATIENT_KEY) ?? '',
  );
  const [providerIdInput, setProviderIdInput] = useState<string>(
    () => localStorage.getItem(PROVIDER_KEY) ?? '',
  );

  const openConfigure = (target: 'patient' | 'provider' | null) => {
    // Re-sync inputs from localStorage in case another card on this page
    // already saved a value while this card's state was stale.
    setPatientIdInput(localStorage.getItem(PATIENT_KEY) ?? '');
    setProviderIdInput(localStorage.getItem(PROVIDER_KEY) ?? '');
    setPendingTarget(target);
    setConfigureOpen(true);
  };

  const sendToSession = async (target: 'patient' | 'provider'): Promise<boolean> => {
    const targetId = getStoredId(target);
    if (!targetId) {
      // First-time use (or value cleared) — pop the configure modal and
      // remember which target the user clicked so we can auto-resend after
      // they save.
      openConfigure(target);
      return false;
    }
    // Fail loudly if the agent record is missing the routing identifiers.
    // This is the bug we hit on FOLFOX agent 99 — the registry shape vs the
    // detail-store shape diverged silently. Prefer to surface it now with
    // a clear toast than have the patient app's listener bail with
    // "missing prompt or agentName" minutes later in DevTools.
    const agentName = ownerAgent?.a2AAgentName;
    const agentDisplayName = ownerAgent?.displayName;
    if (!agentName) {
      // eslint-disable-next-line no-console
      console.error('[sendToSession] missing a2AAgentName on owner agent', {
        ownerAgent,
        promptId: prompt.id,
        promptAgentId: prompt.aiAgentId,
      });
      void message.error(
        'Cannot send: this agent is missing its A2A agent name (a2AAgentName). ' +
        'Open the Agent detail page and verify "A2A agent name" is set, then retry.',
      );
      return false;
    }
    // Verbose entry log so a future failure is one console scroll away from
    // diagnosis — same pattern as the patient app's listener log.
    // eslint-disable-next-line no-console
    console.log('[sendToSession] →', {
      target,
      targetUserId: targetId,
      agentName,
      agentDisplayName,
      promptId: prompt.id,
      promptLen: prompt.prompt?.length,
    });
    try {
      await sendTestPromptToSession({
        targetUserId: targetId,
        systemId: target === 'patient' ? 1 : 2,
        prompt: prompt.prompt,
        agentName,
        agentDisplayName,
      });
      const idLabel = target === 'patient' ? 'patient member id' : 'provider employee id';
      void message.success({
        content: (
          <span>
            Sent to <strong>{target}</strong> tab signed in with {idLabel}{' '}
            <code>{targetId}</code>. If nothing appears, that tab isn't open or
            isn't signed in with that id —{' '}
            <a onClick={() => openConfigure(target)}>reconfigure</a>.
          </span>
        ),
        duration: 6,
      });
      return true;
    } catch (err) {
      void message.error('Send failed: ' + (err as Error).message + ' — falling back to new-tab launch.');
      return false;
    }
  };

  // Save handler — persists both ids and, if the user came in via "Test in
  // {patient|provider} app", auto-fires that send so they don't have to
  // click twice.
  const handleSaveConfig = async () => {
    const pid = patientIdInput.trim();
    const eid = providerIdInput.trim();
    if (pid) localStorage.setItem(PATIENT_KEY, pid);
    else localStorage.removeItem(PATIENT_KEY);
    if (eid) localStorage.setItem(PROVIDER_KEY, eid);
    else localStorage.removeItem(PROVIDER_KEY);
    setConfigureOpen(false);
    if (pendingTarget) {
      const t = pendingTarget;
      setPendingTarget(null);
      // Use a microtask defer so the modal is fully closed before the toast
      // appears — prevents a flicker where the success toast renders behind
      // the still-fading modal mask.
      await Promise.resolve();
      void sendToSession(t);
    }
  };

  // Primary click: try SignalR send; on failure / user-cancel, fall back to
  // opening the URL deep-link (preserves the previous behavior so testing
  // never gets blocked). When the env URL is also missing, we just bail.
  const handlePrimaryClick = async () => {
    const ok = await sendToSession(defaultTarget);
    if (!ok && defaultLink) openLink(defaultLink);
  };

  // (Chevron-menu items are now built inline in the JSX below — they need
  // direct references to sendToSession/openLink, so a memo doesn't help.)

  const tags = (prompt.tags ?? '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  // Stale = last tested 30+ days ago. Helps the user spot drift.
  const stale = prompt.lastTestedAtUtc
    ? Date.now() - new Date(prompt.lastTestedAtUtc).getTime() > 30 * 24 * 60 * 60 * 1000
    : true;

  const result = prompt.lastResultStatus;
  const resultMeta = result ? RESULT_TONE[result] : null;

  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderLeft: `4px solid ${
          prompt.safetyCritical
            ? colors.red[600]
            : prompt.peerConsultationExpected
            ? colors.violet[500]
            : colors.slate[300]
        }`,
        borderRadius: 10,
        padding: 14,
        opacity: stale && result ? 0.85 : 1,
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {prompt.code && (
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 11,
              fontWeight: 700,
              padding: '2px 7px',
              background: colors.violet[600],
              color: '#FFFFFF',
              borderRadius: 4,
            }}
          >
            {prompt.code}
          </span>
        )}
        <Text style={{ fontWeight: 600, flex: 1, color: isDark ? colors.slate[50] : colors.slate[900] }}>
          {prompt.title ?? '(untitled)'}
        </Text>
        {prompt.role && <Tag color="blue">{prompt.role}</Tag>}
        {prompt.safetyCritical && (
          <Tag color="red" icon={<WarningFilled />}>
            Safety
          </Tag>
        )}
        {prompt.peerConsultationExpected && <Tag color="purple">Peer consult</Tag>}
        {resultMeta && (
          <Tooltip
            title={
              <div>
                <div>Last result: {resultMeta.label}</div>
                {prompt.lastTestedAtUtc && (
                  <div style={{ marginTop: 4, opacity: 0.8 }}>
                    {new Date(prompt.lastTestedAtUtc).toLocaleString()}
                  </div>
                )}
                {prompt.lastResultNotes && (
                  <div style={{ marginTop: 4, fontStyle: 'italic' }}>{prompt.lastResultNotes}</div>
                )}
              </div>
            }
          >
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 999,
                background: resultMeta.bg,
                color: resultMeta.fg,
              }}
            >
              {resultMeta.label}
            </span>
          </Tooltip>
        )}
      </div>

      {/* Prompt body — the big copy moment. Code-style block, big Copy button. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) auto',
          gap: 12,
          alignItems: 'stretch',
          marginBottom: 10,
        }}
      >
        <pre
          style={{
            margin: 0,
            padding: '10px 12px',
            background: isDark ? colors.slate[900] : colors.slate[50],
            border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
            borderRadius: 8,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12.5,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: isDark ? colors.slate[100] : colors.slate[800],
            maxHeight: 240,
            overflow: 'auto',
          }}
        >
          {prompt.prompt}
        </pre>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 150 }}>
          <Button
            type="primary"
            size="large"
            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
            onClick={copy}
            block
          >
            {copied ? 'Copied!' : 'Copy'}
          </Button>
          <Dropdown.Button
            size="middle"
            icon={<ExportOutlined />}
            onClick={() => void handlePrimaryClick()}
            menu={{
              items: [
                // Send to the OTHER app's open tab via SignalR.
                ...(defaultTarget !== 'patient'
                  ? [{
                      key: 'send-patient',
                      label: 'Send to open patient tab',
                      onClick: () => void sendToSession('patient'),
                    }]
                  : []),
                ...(defaultTarget !== 'provider'
                  ? [{
                      key: 'send-provider',
                      label: 'Send to open provider tab',
                      onClick: () => void sendToSession('provider'),
                    }]
                  : []),
                // URL deep-link fallback — opens a new tab. Useful when no
                // session is open under that user, or when SignalR is down.
                ...(patientLink
                  ? [{
                      key: 'open-patient',
                      label: 'Open new patient tab (with prompt in URL)',
                      onClick: () => openLink(patientLink),
                    }]
                  : []),
                ...(providerLink
                  ? [{
                      key: 'open-provider',
                      label: 'Open new provider tab (with prompt in URL)',
                      onClick: () => openLink(providerLink),
                    }]
                  : []),
                { type: 'divider' as const },
                {
                  key: 'configure',
                  icon: <SettingOutlined />,
                  label: 'Configure test session ids…',
                  onClick: () => openConfigure(null),
                },
              ],
            }}
          >
            <Tooltip
              title={
                <span>
                  Sends this prompt to the open <strong>{defaultTarget}</strong> tab signed in
                  with the configured {defaultTarget === 'patient' ? 'member id' : 'employee id'}.
                  Click the chevron → "Configure test session ids…" to change it.
                </span>
              }
            >
              <span>{defaultLabel}</span>
            </Tooltip>
          </Dropdown.Button>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────────
          Configure test session ids — first-click modal & change-anytime
          modal. The user enters the member id (patient app) or employee id
          (provider app) of the test fixture they're signed into. Persisted
          to localStorage so subsequent clicks land directly. They can re-
          open this from: (1) the chevron menu, (2) the page-level Settings
          button, or (3) the "reconfigure" link in the success toast.
          ───────────────────────────────────────────────────────────────── */}
      <Modal
        open={configureOpen}
        title="Configure test session"
        okText={pendingTarget ? 'Save and send' : 'Save'}
        cancelText="Cancel"
        onOk={() => void handleSaveConfig()}
        onCancel={() => {
          setConfigureOpen(false);
          setPendingTarget(null);
        }}
        destroyOnHidden
      >
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Sign into the patient and/or provider app first as the test user
          you want to receive this prompt. Open the browser DevTools console
          on that tab — you'll see a line like:
        </Paragraph>
        <pre
          style={{
            margin: '8px 0',
            padding: '8px 10px',
            background: '#F8FAFC',
            border: '1px solid #E2E8F0',
            borderRadius: 6,
            fontSize: 11.5,
            fontFamily: "'JetBrains Mono', monospace",
            whiteSpace: 'pre-wrap',
          }}
        >
          {`[SignalR] Connected to notification hub. Subscribed to groups: "1234-1" …
For Agent Studio "Test in patient app", use TargetUserId=1234`}
        </pre>
        <Paragraph type="secondary" style={{ fontSize: 13 }}>
          Copy the number (<code>1234</code> in the example above) and paste
          it below.
        </Paragraph>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Patient app — member id
            </Text>
            <Input
              placeholder="e.g. 1234"
              value={patientIdInput}
              onChange={(e) => setPatientIdInput(e.target.value)}
              autoFocus={pendingTarget !== 'provider'}
              onPressEnter={() => void handleSaveConfig()}
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 4 }}>
              Provider app — employee id
            </Text>
            <Input
              placeholder="e.g. 5678"
              value={providerIdInput}
              onChange={(e) => setProviderIdInput(e.target.value)}
              autoFocus={pendingTarget === 'provider'}
              onPressEnter={() => void handleSaveConfig()}
            />
          </div>
        </div>
        <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
          Stored in this browser only (<code>localStorage</code>). Leave a
          field blank to clear it.
        </Paragraph>
      </Modal>

      {/* What this tests — italic single line */}
      {prompt.whatThisTests && (
        <Paragraph
          style={{
            margin: '0 0 8px',
            fontSize: 12.5,
            color: isDark ? colors.slate[300] : colors.slate[600],
            fontStyle: 'italic',
          }}
        >
          <strong style={{ fontStyle: 'normal' }}>Tests: </strong>
          {prompt.whatThisTests}
        </Paragraph>
      )}

      {/* What to look for — preserve newlines for the bullet checklist */}
      {prompt.whatToLookFor && (
        <div
          style={{
            background: isDark ? colors.slate[900] + '88' : colors.slate[50],
            border: `1px dashed ${isDark ? colors.slate[700] : colors.slate[200]}`,
            borderRadius: 6,
            padding: '8px 10px',
            marginBottom: 10,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: isDark ? colors.slate[400] : colors.slate[500],
              marginBottom: 4,
            }}
          >
            What to look for
          </div>
          <pre
            style={{
              margin: 0,
              fontSize: 12.5,
              fontFamily: 'inherit',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: isDark ? colors.slate[200] : colors.slate[700],
            }}
          >
            {prompt.whatToLookFor}
          </pre>
        </div>
      )}

      {tags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
          {tags.map((t) => (
            <Tag key={t}>{t}</Tag>
          ))}
        </div>
      )}

      {/* Action row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <Space size={4}>
          <Tooltip title="Mark this run as a pass">
            <Button
              size="small"
              icon={<CheckOutlined />}
              onClick={() => onMark('pass')}
              style={{ color: colors.green[700], borderColor: colors.green[100] }}
            >
              Pass
            </Button>
          </Tooltip>
          <Tooltip title="Mark this run as a fail">
            <Button size="small" danger icon={<CloseOutlined />} onClick={() => onMark('fail')}>
              Fail
            </Button>
          </Tooltip>
          <Tooltip title="Skip — not run this round">
            <Button size="small" icon={<StopOutlined />} onClick={() => onMark('skip')}>
              Skip
            </Button>
          </Tooltip>
        </Space>
        <div style={{ flex: 1 }} />
        <Tooltip title="Edit">
          <Button type="text" size="small" icon={<EditOutlined />} onClick={onEdit} />
        </Tooltip>
        <Popconfirm
          title="Remove this prompt?"
          description="The prompt is removed from this agent's library. Other agents are unaffected."
          okText="Remove"
          okButtonProps={{ danger: true }}
          onConfirm={onDelete}
        >
          <Tooltip title="Remove">
            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>
      </div>
    </div>
  );
}
